"use client";

import { Check, ChevronRight, Lock, Mail, Phone, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const industryOptions = ["Professional Services", "E-commerce", "Local Retail", "Healthcare", "Hospitality", "Other"];
const assessmentTypeOptions = ["Website Design", "SEO Strategy", "Local SEO Audit", "Full Digital Growth Plan"];
const trustItems = ["Non-disclosure agreement for all project data", "Initial consultation within 4 business hours", "Custom growth roadmap within 48 hours"];
function AssessmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center">
            <Check className="size-5 text-primary" />
          </div>
          <div className="font-mono text-xs tracking-[0.18em] uppercase text-primary">
            REQUEST RECEIVED
          </div>
        </div>
        <p className="text-foreground text-lg mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="company_name" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Business Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Business Name" className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Enter your registered business name.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="contact_name" rules={{
      required: "Contact person is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Contact Person
            </FormLabel>
            <FormControl>
              <Input placeholder="Full Name" className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Who should we address our response to?
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@business.com" className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="industry" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Industry
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11 w-full">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {industryOptions.map((option, i) => <SelectItem key={option} data-index={i} value={option}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="assessment_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Service Interest
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {assessmentTypeOptions.map((option, i) => <Label key={option} data-index={i} htmlFor={`service-${i}`} className="flex items-center gap-3 p-3 bg-secondary/40 border border-border rounded-md cursor-pointer hover:border-primary/50 transition-colors">
                    <RadioGroupItem value={option} id={`service-${i}`} />
                    <span className="text-sm text-foreground">{option}</span>
                  </Label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Target Start Date
            </FormLabel>
            <FormControl>
              <Input type="date" className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 h-11" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              When are you looking to begin?
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Tell us about your goals or current challenges..." className="bg-secondary/40 border-border focus-visible:border-primary focus-visible:ring-primary/30 min-h-32" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Separator className="bg-border" />

      <div className="flex items-start gap-3 p-4 bg-secondary/30 border border-border rounded-md">
        <Checkbox id="nda-consent" className="mt-0.5" />
        <Label htmlFor="nda-consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
          I agree to share my business details for the purpose of receiving a custom growth proposal.
        </Label>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="space-y-3">
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold tracking-wide">
          <Lock className="size-4" />
          {isSubmitting ? "SENDING..." : "REQUEST GROWTH PROPOSAL"}
          <ChevronRight className="size-4" />
        </Button>
        <p className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground text-center">
          TYPICAL RESPONSE: 4 BUSINESS HOURS
        </p>
      </div>
    </Form>;
}
export default function StartYourJourney() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="386" id="start-your-journey" className="relative bg-background text-foreground overflow-hidden py-32 md:py-40 px-4 md:px-8">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "48px 48px",
      backgroundPosition: "right bottom"
    }} />

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -inset-1/2 opacity-20" style={{
        background: "linear-gradient(115deg, transparent 40%, var(--primary) 50%, transparent 60%)",
        animation: "beamSweep 8s linear infinite"
      }} />
      </div>

      <style>{`
        @keyframes beamSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
      `}</style>

      <div ref={ref} className={`relative mx-auto max-w-7xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="flex items-center gap-2.5 mb-8">
                <span className="inline-block size-2 rounded-md bg-chart-4" style={{
                animation: "pulseDot 2s ease-in-out infinite"
              }} aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  GET STARTED
                </span>
              </div>

              <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-6">
                Start your{" "}
                <span className="relative inline-block text-primary">
                  <span aria-hidden="true" className="absolute -left-2 -top-2 text-primary/70 font-mono text-base">
                    +
                  </span>
                  growth journey
                  <span aria-hidden="true" className="absolute -right-2 -bottom-2 text-primary/70 font-mono text-base">
                    ¬
                  </span>
                </span>
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-xl">
                Partner with Asahi Search to elevate your digital presence. 
                Whether you need a new website, SEO, or local Google listing support, 
                we are ready to help your business scale.
              </p>

              <ul className="space-y-3.5 mb-10">
                {trustItems.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-3">
                    <div className="mt-0.5 size-5 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Check className="size-3 text-primary" />
                    </div>
                    <span className="text-sm text-foreground/90 leading-relaxed">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <Separator className="bg-border mb-10" />

              <div className="space-y-5">
                <div className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground mb-2">
                  CONTACT US
                </div>

                <div className="flex items-start gap-3">
                  <span className="mt-1.5 inline-block size-2 rounded-md bg-destructive shrink-0" style={{
                  animation: "pulseDot 1.5s ease-in-out infinite"
                }} aria-hidden="true" />
                  <div>
                    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-destructive mb-1">
                      DIRECT LINE
                    </div>
                    <div className="font-mono text-sm text-foreground">+1 (475) 332-6003</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="size-4 text-primary mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                      EMAIL
                    </div>
                    <div className="font-mono text-sm text-foreground break-all">asahisearch@gmail.com</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Shield className="size-4 text-primary mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                      SUPPORT
                    </div>
                    <div className="font-mono text-sm text-foreground">
                      MON-FRI: 9AM - 6PM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative bg-card border border-border rounded-2xl shadow-2xl p-6 sm:p-10 lg:p-12">
              <div aria-hidden="true" className="absolute -top-px -left-px size-8 border-t border-l border-primary rounded-tl-2xl" />
              <div aria-hidden="true" className="absolute -top-px -right-px size-8 border-t border-r border-primary rounded-tr-2xl" />
              <div aria-hidden="true" className="absolute -bottom-px -left-px size-8 border-b border-l border-primary rounded-bl-2xl" />
              <div aria-hidden="true" className="absolute -bottom-px -right-px size-8 border-b border-r border-primary rounded-br-2xl" />

              <div className="flex items-center justify-between mb-8 pb-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <Shield className="size-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-default text-base font-semibold text-foreground">
                      Growth Inquiry
                    </div>
                    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
                      SECURE FORM
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground hidden sm:block">ASAHI-2026</div>
              </div>

              <FormProvider formKey="start_journey_form_home" formId="start_journey_form_home" sectionName="start-your-journey" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              industry: "",
              assessment_type: "Website Design",
              timeline: "",
              details: ""
            }} validationRules={{
              company_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              assessment_type: {
                required: true
              }
            }} submitText="Request Growth Proposal" successMessage="Your inquiry has been received. Our team will review your details and contact you within 4 business hours." errorMessage="Submission failed. Please try again or contact us directly.">
                <AssessmentForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}